import type { ClipboardEvent, KeyboardEvent } from 'react';
import {
  formatPanNumber,
  formatAadhaarNumber,
  formatPassportNumber,
  formatAccountNumber,
  formatIfscCode,
  validatePanNumber,
  validateAadhaarNumber,
  validatePassportNumber,
  validateAccountNumber,
  validateIfscCode,
  validateRoutingNumber,
  validateSortCode,
  validateBsbNumber,
  validateIban,
  validateBicSwift,
  validateInstitutionNumber,
  validateTransitNumber,
  DOCUMENT_VALIDATORS,
} from './documentValidation';

export {
  formatPanNumber,
  formatAadhaarNumber,
  formatPassportNumber,
  formatAccountNumber,
  formatIfscCode,
  validatePanNumber,
  validateAadhaarNumber,
  validatePassportNumber,
  validateAccountNumber,
  validateIfscCode,
  validateRoutingNumber,
  validateSortCode,
  validateBsbNumber,
  validateIban,
  validateBicSwift,
  validateInstitutionNumber,
  validateTransitNumber,
  DOCUMENT_VALIDATORS,
};

export const VALIDATION_MESSAGES = {
  fullName: {
    required: 'Full name is required',
    minLength: 'Full name must be at least 2 characters',
    maxLength: 'Full name cannot exceed 50 characters',
    pattern: 'Full name can only contain letters and spaces',
  },
  email: {
    required: 'Email is required',
    invalid: 'Please enter a valid email address',
    maxLength: 'Email cannot exceed 100 characters',
  },
  phone: {
    required: 'Phone number is required',
    invalid: 'Please enter a valid phone number',
    minLength: 'Phone number must be at least 10 digits',
    maxLength: 'Phone number cannot exceed 15 digits',
  },
  password: {
    required: 'Password is required',
    minLength: 'Password must be at least 8 characters',
    maxLength: 'Password cannot exceed 20 characters',
    weak: 'Password must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character',
    mismatch: 'Passwords do not match',
  },
  confirmPassword: {
    required: 'Please confirm your password',
    mismatch: 'Passwords do not match',
  },

  identityDocument: {
    required: 'Identity document is required',
    invalidType: 'Please upload a valid document (PDF, JPG, PNG)',
    maxSize: 'File size cannot exceed 5MB',
    invalidFormat: 'Document must be in PDF, JPG, or PNG format',
  },
  facialVerification: {
    required: 'Facial verification is required',
    invalidType: 'Please upload a valid image (JPG, PNG)',
    maxSize: 'Image size cannot exceed 2MB',
    invalidFormat: 'Image must be in JPG or PNG format',
  },
  addressDocument: {
    required: 'Address document is required',
    invalidType: 'Please upload a valid document (PDF, JPG, PNG)',
    maxSize: 'File size cannot exceed 5MB',
    invalidFormat: 'Document must be in PDF, JPG, or PNG format',
  },
  panNumber: {
    required: 'PAN number is required',
    invalid: 'Please enter a valid PAN number',
    pattern: 'PAN number must be in format: ABCDE1234F',
  },
  panDocument: {
    required: 'PAN card document is required',
    invalidType: 'Please upload a valid document (PDF, JPG, PNG)',
    maxSize: 'File size cannot exceed 5MB',
    invalidFormat: 'Document must be in PDF, JPG, or PNG format',
  },
  passportNumber: {
    required: 'Passport number is required',
    invalid: 'Please enter a valid Passport number',
    pattern: 'Passport number must be 6-9 alphanumeric characters',
  },
  passportDocument: {
    required: 'Passport document is required',
    invalidType: 'Please upload a valid document (PDF, JPG, PNG)',
    maxSize: 'File size cannot exceed 5MB',
    invalidFormat: 'Document must be in PDF, JPG, or PNG format',
  },
  accountName: {
    required: 'Account name is required',
    minLength: 'Account name must be at least 3 characters',
    maxLength: 'Account name cannot exceed 100 characters',
    pattern:
      'Account name can only contain letters, spaces, and common punctuation',
  },
  accountNumber: {
    required: 'Account number is required',
    minLength: 'Account number must be at least 8 digits',
    maxLength: 'Account number cannot exceed 20 digits',
    pattern: 'Account number can only contain numbers',
  },
  bankName: {
    required: 'Bank name is required',
    minLength: 'Bank name must be at least 3 characters',
    maxLength: 'Bank name cannot exceed 100 characters',
    pattern:
      'Bank name can only contain letters, spaces, and common punctuation',
  },
  branchName: {
    required: 'Branch name is required',
    minLength: 'Branch name must be at least 3 characters',
    maxLength: 'Branch name cannot exceed 100 characters',
    pattern:
      'Branch name can only contain letters, spaces, and common punctuation',
  },
  ifscCode: {
    required: 'IFSC code is required',
    invalid: 'Please enter a valid IFSC code',
    pattern: 'IFSC code must be in format: ABCD0123456',
  },
  routingNumber: {
    required: 'Routing number is required',
    invalid: 'Please enter a valid Routing number',
    pattern: 'Routing number must be exactly 9 digits',
  },
  sortCode: {
    required: 'Sort code is required',
    invalid: 'Please enter a valid Sort code',
    pattern: 'Sort code must be 6 digits (e.g. 123456 or 12-34-56)',
  },
  bsbNumber: {
    required: 'BSB number is required',
    invalid: 'Please enter a valid BSB number',
    pattern: 'BSB number must be 6 digits (e.g. 123456 or 123-456)',
  },
  iban: {
    required: 'IBAN is required',
    invalid: 'Please enter a valid IBAN',
    pattern: 'Please enter a valid IBAN (e.g. DE89370400440532013000)',
  },
  bicSwift: {
    required: 'BIC / SWIFT code is required',
    invalid: 'Please enter a valid BIC / SWIFT code',
    pattern: 'BIC / SWIFT code must be 8 or 11 characters (e.g. DBKDEFFXXXX)',
  },
  bankCountry: {
    required: 'Bank country is required',
  },

  aadharNumber: {
    required: 'Aadhar number is required',
    invalid: 'Please enter a valid Aadhar number',
    pattern: 'Aadhar number must be 12 digits',
  },
  verificationDocument: {
    required: 'Verification document is required',
    invalidType: 'Please upload a valid image or PDF',
    maxSize: 'File size cannot exceed 10MB',
    invalidFormat: 'File must be in JPG, PNG, WEBP, or PDF format',
  },

  referralCode: {
    invalid: 'Referral code must contain at least 10 characters',
    maxLength: 'Referral code cannot exceed 10 characters',
  },
  source: {
    required: 'Please select how you heard about us',
  },

  success: {
    emailVerified: 'Email verified successfully',
    phoneVerified: 'Phone verified successfully',
    facialVerificationCompleted: 'Facial verification completed',
    documentUploaded: 'Document uploaded successfully',
    registrationComplete: 'Registration completed successfully',
  },

  error: {
    generic: 'An error occurred. Please try again.',
    network: 'Network error. Please check your connection.',
    timeout: 'Request timed out. Please try again.',
    unauthorized: 'You are not authorized to perform this action.',
    forbidden: 'You do not have permission to perform this action.',
    notFound: 'The requested resource was not found.',
    serverError: 'Server error. Please try again later.',
  },
};

export const FILE_CONSTRAINTS = {
  maxFileSize: {
    document: 5 * 1024 * 1024,
    image: 2 * 1024 * 1024,
    cheque: 5 * 1024 * 1024,
  },
  allowedTypes: {
    document: ['application/pdf', 'image/jpeg', 'image/png'],
    image: ['image/jpeg', 'image/png'],
    cheque: ['image/jpeg', 'image/png'],
  },
  allowedExtensions: {
    document: ['.pdf', '.jpg', '.jpeg', '.png'],
    image: ['.jpg', '.jpeg', '.png'],
    cheque: ['.jpg', '.jpeg', '.png'],
  },
};

export const MIN_TRANSACTION_AMOUNT = 100;
export const MAX_TRANSACTION_AMOUNT = 10000;
export const MIN_COMMENT_LENGTH = 15;
export const MAX_COMMENT_LENGTH = 200;

export const NUMBER_INPUT_BLOCKED_KEYS = ['e', 'E', '+', '-'];

export const preventInvalidNumberInput = (
  event: KeyboardEvent<HTMLInputElement>
) => {
  if (NUMBER_INPUT_BLOCKED_KEYS.includes(event.key)) {
    event.preventDefault();
  }
};

export const preventInvalidNumberPaste = (
  event: ClipboardEvent<HTMLInputElement>
) => {
  if (/[eE+-]/.test(event.clipboardData.getData('text'))) {
    event.preventDefault();
  }
};

export const preventInvalidNameInput = (
  event: KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    'Shift',
    'Control',
    'Alt',
    'CapsLock',
    'Escape',
  ];
  if (allowedKeys.includes(event.key)) return;
  if (!/^[a-zA-Z \-']$/.test(event.key)) {
    event.preventDefault();
  }
};

export const getCommentValidationMessage = (
  value: string,
  fieldName = 'Comment'
) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return `${fieldName} is required.`;
  }

  if (trimmedValue.length < MIN_COMMENT_LENGTH) {
    return `${fieldName} must be at least ${MIN_COMMENT_LENGTH} characters.`;
  }

  if (trimmedValue.length > MAX_COMMENT_LENGTH) {
    return `${fieldName} cannot exceed ${MAX_COMMENT_LENGTH} characters.`;
  }

  return undefined;
};

export const REGEX_PATTERNS = {
  name: /^[a-zA-Z\s]+$/,
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  phone: /^\+?[1-9]\d{9,14}$/,
  pan: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
  aadhar: /^\d{12}$/,
  passport: /^[A-Z0-9]{6,9}$/,
  ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/,
  accountNumber: /^\d{8,20}$/,
  routingNumber: /^\d{9}$/,
  sortCode: /^(\d{6}|\d{2}-\d{2}-\d{2})$/,
  bsbNumber: /^(\d{6}|\d{3}-\d{3})$/,
  iban: /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/,
  bicSwift: /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
  accountName: /^[a-zA-Z\s\-'.&,]+$/,
  bankBranch: /^[a-zA-Z0-9\s\-'.&,]+$/,
  password: {
    basic: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/,
    strong:
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&].{8,}$/,
  },
};

export const VALIDATORS = {
  email: (value: string): boolean => {
    const domainPart = value.split('@')[1];
    if (!domainPart) return false;
    const parts = domainPart.split('.');
    const tld = parts[parts.length - 1];
    if (!/^[a-zA-Z]{2,3}$/.test(tld)) {
      return false;
    }
    if (domainPart.includes('..') || /[^a-zA-Z0-9.-]/.test(domainPart)) {
      return false;
    }

    return true;
  },
};

export interface ValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

export interface FundFormData {
  name: string;
  description: string;
  currency: string;
  minimumInvestment: number | string;
  maximumInvestment: number | string;
  performanceFee: number | string;
  managementFee: number | string;
  entryFee: number | string;
  exitFee: number | string;
  riskLevel: string;
  leverage: string;
  allocationMethod: string;
  lockPeriodDays: number | string;
}

export const validateFundForm = (
  data: Partial<FundFormData>
): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!data.name?.trim()) {
    errors.name = 'Fund name is required.';
  } else if (data.name.trim().length < 3) {
    errors.name = 'Fund name must be at least 3 characters.';
  } else if (data.name.trim().length > 100) {
    errors.name = 'Fund name must be under 100 characters.';
  } else if (!/^[a-zA-Z \-']+$/.test(data.name.trim())) {
    errors.name = VALIDATION_MESSAGES.fullName.pattern;
  }

  if (!data.description?.trim()) {
    errors.description = 'Description is required.';
  } else if (data.description.trim().length < 20) {
    errors.description = 'Description must be at least 20 characters.';
  }

  if (!data.currency) {
    errors.currency = 'Currency is required.';
  }

  const minInv = Number(data.minimumInvestment);
  if (!data.minimumInvestment && data.minimumInvestment !== 0) {
    errors.minimumInvestment = 'Minimum investment is required.';
  } else if (isNaN(minInv) || minInv < 100) {
    errors.minimumInvestment = 'Minimum investment must be at least $100.';
  }

  const maxInv = Number(data.maximumInvestment);
  if (!data.maximumInvestment && data.maximumInvestment !== 0) {
    errors.maximumInvestment = 'Maximum investment is required.';
  } else if (isNaN(maxInv) || maxInv < minInv) {
    errors.maximumInvestment =
      'Maximum must be greater than minimum investment.';
  }

  const perfFee = Number(data.performanceFee);
  if (data.performanceFee === undefined || data.performanceFee === '') {
    errors.performanceFee = 'Performance fee is required.';
  } else if (isNaN(perfFee) || perfFee < 0 || perfFee > 50) {
    errors.performanceFee = 'Performance fee must be between 0% and 50%.';
  }

  const mgmtFee = Number(data.managementFee);
  if (data.managementFee === undefined || data.managementFee === '') {
    errors.managementFee = 'Management fee is required.';
  } else if (isNaN(mgmtFee) || mgmtFee < 0 || mgmtFee > 10) {
    errors.managementFee = 'Management fee must be between 0% and 10%.';
  }

  if (!data.riskLevel) {
    errors.riskLevel = 'Risk level is required.';
  }

  if (!data.leverage) {
    errors.leverage = 'Leverage is required.';
  }

  if (!data.allocationMethod) {
    errors.allocationMethod = 'Allocation method is required.';
  }

  const lockDays = Number(data.lockPeriodDays);
  if (data.lockPeriodDays === undefined || data.lockPeriodDays === '') {
    errors.lockPeriodDays = 'Lock period is required.';
  } else if (isNaN(lockDays) || lockDays < 0) {
    errors.lockPeriodDays = 'Lock period must be 0 or more days.';
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validateSettingsForm = (data: any): ValidationResult => {
  const errors: Record<string, string> = {};

  if (
    data?.fees?.defaultPerformanceFee < 0 ||
    data?.fees?.defaultPerformanceFee > 50
  ) {
    errors.defaultPerformanceFee =
      'Performance fee must be between 0% and 50%.';
  }

  if (
    data?.fees?.defaultManagementFee < 0 ||
    data?.fees?.defaultManagementFee > 10
  ) {
    errors.defaultManagementFee = 'Management fee must be between 0% and 10%.';
  }

  if (data?.risk?.maxDrawdownLimit < 1 || data?.risk?.maxDrawdownLimit > 100) {
    errors.maxDrawdownLimit = 'Drawdown limit must be between 1% and 100%.';
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

export interface PammInvestmentFormData {
  pammId: string;
  investmentAmount: number | string;
  walletBalance: number;
  minInvestment?: number;
  maxInvestment?: number;
}

export const validatePammInvestmentForm = (
  data: Partial<PammInvestmentFormData>
): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!data.pammId) {
    errors.pammId = 'PAMM selection is required.';
  }

  const amount = Number(data.investmentAmount);
  if (data.investmentAmount === undefined || data.investmentAmount === '') {
    errors.investmentAmount = 'Investment amount is required.';
  } else if (isNaN(amount) || amount <= 0) {
    errors.investmentAmount =
      'Investment amount must be a positive number greater than zero.';
  } else if (data.walletBalance !== undefined && amount > data.walletBalance) {
    errors.investmentAmount = `Investment amount cannot exceed available wallet balance ($${data.walletBalance.toLocaleString()}).`;
  } else if (data.minInvestment !== undefined && amount < data.minInvestment) {
    errors.investmentAmount = `Minimum investment requirement for this PAMM is $${data.minInvestment.toLocaleString()}.`;
  } else if (data.maxInvestment !== undefined && amount > data.maxInvestment) {
    errors.investmentAmount = `Maximum investment limit for this PAMM is $${data.maxInvestment.toLocaleString()}.`;
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

export interface PammWithdrawalFormData {
  investorId: string;
  withdrawalType: string;
  withdrawalAmount: number | string;
  comment: string;
  maxAvailableBalance?: number;
}

export const validatePammWithdrawalForm = (
  data: Partial<PammWithdrawalFormData>
): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!data.investorId) {
    errors.investorId = 'Investor selection is required.';
  }

  if (!data.withdrawalType) {
    errors.withdrawalType = 'Withdrawal type is required.';
  }

  const amount = Number(data.withdrawalAmount);
  if (data.withdrawalAmount === undefined || data.withdrawalAmount === '') {
    errors.withdrawalAmount = 'Withdrawal amount is required.';
  } else if (isNaN(amount) || amount <= 0) {
    errors.withdrawalAmount =
      'Withdrawal amount must be a positive number greater than zero.';
  } else if (
    data.maxAvailableBalance !== undefined &&
    amount > data.maxAvailableBalance
  ) {
    errors.withdrawalAmount = `Withdrawal amount cannot exceed available balance ($${data.maxAvailableBalance.toLocaleString()}).`;
  }

  if (!data.comment?.trim()) {
    errors.comment = 'Comment is required.';
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

export default VALIDATION_MESSAGES;
